/**
 * A seleção múltipla do quadro de compras.
 *
 * Papel: guarda os `id` dos pedidos marcados nas caixas do quadro, na ordem em
 * que foram marcados, num estado que vive enquanto a aba do navegador estiver
 * aberta — abrir um pedido em `/compras/:id`, ir a outro módulo e voltar a
 * `/compras` encontra a mesma seleção; recarregar a página a esvazia.
 *
 * Como usar: a tela lê `ids` por `useSelecaoDePedidos` e o cruza com a
 * listagem atual para saber quais pedidos estão marcados — um `id` que não
 * casa com nenhum pedido no quadro continua guardado, mas não conta nem entra
 * em ação em lote. As funções de mudança trocam o conjunto inteiro, e todo
 * componente que usa o hook redesenha junto.
 *
 * A ordem de `ids` é a ordem de marcação, e é a ordem em que a ação em lote
 * grava os pedidos e os põe na folha de aprovação. Marcar de novo um `id` que
 * já está marcado não o move para o fim.
 *
 * O estado mora no módulo, fora do React, e por isso atravessa testes: quem
 * testa a tela deverá chamar `esquecerSelecaoDePedidos` antes de cada caso.
 */
import { useSyncExternalStore } from 'react';

let selecao: ReadonlySet<string> = new Set();
const ouvintes = new Set<() => void>();

/** Troca a seleção e avisa todo componente que a lê. */
function publicar(proxima: ReadonlySet<string>) {
  selecao = proxima;
  for (const ouvinte of ouvintes) ouvinte();
}

/** Inscreve um componente nas mudanças da seleção. */
function assinar(ouvinte: () => void) {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

/** A seleção do momento, para o `useSyncExternalStore`. */
function lerSelecao() {
  return selecao;
}

/**
 * Marca ou desmarca vários pedidos de uma vez.
 *
 * @param ids - Os `id` dos pedidos, na ordem em que entram quando marcados.
 * @param marcado - `true` acrescenta os que faltam; `false` tira todos eles.
 */
function definirVarios(ids: readonly string[], marcado: boolean) {
  const proxima = new Set(selecao);
  for (const id of ids) {
    if (marcado) proxima.add(id);
    else proxima.delete(id);
  }
  publicar(proxima);
}

/**
 * Esvazia a seleção.
 *
 * Quem chama, fora de "Limpar seleção", são os testes da tela, antes de cada
 * caso: sem isso a seleção de um teste aparece marcada no seguinte.
 */
export function esquecerSelecaoDePedidos() {
  publicar(new Set());
}

/**
 * Lê a seleção do quadro e devolve as funções que a mudam.
 *
 * @returns `ids`, os marcados na ordem de marcação; `marcar`, que marca ou
 *   desmarca vários pedidos; e `limpar`, que esvazia a seleção.
 */
export function useSelecaoDePedidos() {
  const ids = useSyncExternalStore(assinar, lerSelecao);
  return { ids, marcar: definirVarios, limpar: esquecerSelecaoDePedidos };
}
